import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { cn } from "@/lib/classNames";

export const ART_PROVIDER = {
  name: "2-Minute Tabletop",
  shortName: "2MTT",
  url: "https://2minutetabletop.com/",
  /** Official mark from https://2minutetabletop.com/ (WP: 2-Minute-Tabletop-Logo-Light-Background.png). */
  logoSrc: "/art-credits/2-minute-tabletop-logo-light.png",
} as const;

export type ArtAttributionBadgeVariant = "canvas-corner" | "dashboard-inline";

type ArtProviderLogoProps = {
  className?: string;
  fallbackClassName?: string;
  alt?: string;
};

export type ArtAttributionBadgeProps = {
  variant: ArtAttributionBadgeVariant;
  className?: string;
};

const BADGE_CLASS_NAMES: Record<ArtAttributionBadgeVariant, string> = {
  "canvas-corner":
    "group inline-flex max-w-full items-center gap-2 rounded-full border border-neutral-950/20 bg-neutral-950/50 px-3 py-1.5 text-white/90 shadow-lg backdrop-blur-md transition-colors hover:bg-neutral-950/65 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3e664d]",
  "dashboard-inline":
    cn(
      "group inline-flex min-h-12 max-w-full items-center gap-3 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-950 transition-colors hover:bg-[#fff2d9] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3e664d]",
      MONOLITH_BOARD_STYLE.dashboardHeaderPanelClassName,
    ),
};

export function ArtProviderLogo({
  className,
  fallbackClassName,
  alt = `${ART_PROVIDER.name} logo`,
}: ArtProviderLogoProps) {
  const [imageFailed, setImageFailed] = useState(false);

  if (imageFailed) {
    return (
      <span
        role="img"
        aria-label={alt}
        className={cn(
          "inline-flex items-center justify-center font-black uppercase tracking-[0.18em]",
          fallbackClassName,
        )}
      >
        {ART_PROVIDER.shortName}
      </span>
    );
  }

  return (
    <img
      src={ART_PROVIDER.logoSrc}
      alt={alt}
      className={className}
      onError={() => setImageFailed(true)}
    />
  );
}

export default function ArtAttributionBadge({
  variant,
  className,
}: ArtAttributionBadgeProps) {
  const isDashboardInline = variant === "dashboard-inline";
  const isCanvasCorner = variant === "canvas-corner";

  return (
    <Link
      to="/credits"
      aria-label="View art credits"
      className={cn(BADGE_CLASS_NAMES[variant], className)}
    >
      {isCanvasCorner ? (
        <>
          <ArtProviderLogo
            className="h-4 w-auto max-w-16 shrink-0 object-contain object-left brightness-0 invert"
            fallbackClassName="h-4 min-w-10 max-w-16 rounded border border-white/30 px-1.5 text-[8px] text-white/95"
          />
          <span className="min-w-0 whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.18em]">
            Art by 2-Minute Tabletop
          </span>
        </>
      ) : (
        <>
          <ArtProviderLogo
            className={cn(
              "w-auto shrink-0",
              isDashboardInline ? "h-6 max-w-20" : "h-5 max-w-16",
            )}
            fallbackClassName={cn(
              "shrink-0",
              isDashboardInline
                ? "min-h-6 min-w-12 border-2 border-neutral-950 px-2 text-[9px]"
                : "min-h-5 min-w-10 border border-white/30 px-1.5 text-[8px] text-white/95",
            )}
          />
          <span className="min-w-0 truncate">
            <span className="hidden sm:inline">Hex art by {ART_PROVIDER.name}</span>
            <span className="sm:hidden">{ART_PROVIDER.shortName}</span>
          </span>
        </>
      )}
    </Link>
  );
}
